const DB_NAME = 'PrijavaProjekatFiles';
const DB_VERSION = 1;
const STORE_NAME = 'files';

// Open the database
export const openDatabase = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = (event) => {
      console.error('Error opening IndexedDB:', event.target.error);
      reject(event.target.error);
    };
    
    request.onsuccess = (event) => {
      resolve(event.target.result);
    };
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      
      // Create object store if it doesn't exist
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const objectStore = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        objectStore.createIndex('storageKey', 'storageKey', { unique: false });
      }
    };
  });
};

// Save files to IndexedDB
export const saveFilesToIndexedDB = async (storageKey, files) => {
  if (!files || files.length === 0) return [];
  
  const db = await openDatabase();
  const transaction = db.transaction([STORE_NAME], 'readwrite');
  const objectStore = transaction.objectStore(STORE_NAME);
  
  const filePromises = files.map(file => {
    return new Promise((resolve, reject) => {
      // Create a unique ID for this file
      const fileId = `${storageKey}-${file.name}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      
      // Store the file with metadata
      const fileRecord = {
        id: fileId,
        name: file.name,
        type: file.type,
        size: file.size,
        lastModified: file.lastModified,
        storageKey: storageKey,
        blob: file
      };
      
      const request = objectStore.add(fileRecord);
      
      request.onsuccess = () => {
        // Return the file with the ID attached
        Object.defineProperty(file, 'id', {
          value: fileId,
          enumerable: true,
          configurable: true,
          writable: true
        });
        resolve(file);
      };
      
      request.onerror = (event) => {
        console.error('Error saving file to IndexedDB:', event.target.error);
        reject(event.target.error);
      };
    });
  });
  
  return Promise.all(filePromises);
};

// Get files from IndexedDB
export const getFilesFromIndexedDB = async (storageKey, metadata) => {
  if (!metadata || metadata.length === 0) return [];
  
  const db = await openDatabase();
  const transaction = db.transaction([STORE_NAME], 'readonly');
  const objectStore = transaction.objectStore(STORE_NAME);
  const index = objectStore.index('storageKey');
  
  return new Promise((resolve, reject) => {
    const request = index.getAll(storageKey);
    
    request.onsuccess = (event) => {
      const result = event.target.result;
      
      if (!result || result.length === 0) {
        resolve([]);
        return;
      }
      
      // Match stored files with metadata and create proper File objects
      const filesWithBlobs = metadata
        .map(metaItem => {
          // Find matching file record
          const fileRecord = result.find(record => 
            (metaItem.id && record.id === metaItem.id) || 
            (!metaItem.id && record.name === metaItem.name)
          );
          
          // If found, create a new File object with the blob
          if (fileRecord) {
            const file = new File(
              [fileRecord.blob], 
              fileRecord.name, 
              { 
                type: fileRecord.type,
                lastModified: fileRecord.lastModified 
              }
            );
            
            // Add the ID for future reference
            Object.defineProperty(file, 'id', {
              value: fileRecord.id,
              enumerable: true,
              configurable: true,
              writable: true
            });
            
            return file;
          }
          return null;
        })
        .filter(file => file !== null);
      
      resolve(filesWithBlobs);
    };
    
    request.onerror = (event) => {
      console.error('Error retrieving files from IndexedDB:', event.target.error);
      reject(event.target.error);
    };
  });
};

// Delete a file from IndexedDB
export const deleteFileFromIndexedDB = async (storageKey, fileId) => {
  const db = await openDatabase();
  const transaction = db.transaction([STORE_NAME], 'readwrite');
  const objectStore = transaction.objectStore(STORE_NAME);
  
  return new Promise((resolve, reject) => {
    // First try to find by ID
    const request = objectStore.openCursor();
    
    request.onsuccess = (event) => {
      const cursor = event.target.result;
      
      if (cursor) {
        const record = cursor.value;
        
        // Check if this is the file to delete (either by ID or by combo of storageKey+name)
        if (record.id === fileId || (record.storageKey === storageKey && record.name === fileId)) {
          const deleteRequest = objectStore.delete(record.id);
          
          deleteRequest.onsuccess = () => {
            resolve(true);
          };
          
          deleteRequest.onerror = (event) => {
            console.error('Error deleting file from IndexedDB:', event.target.error);
            reject(event.target.error);
          };
        } else {
          cursor.continue();
        }
      } else {
        // No matching file found
        resolve(false);
      }
    };
    
    request.onerror = (event) => {
      console.error('Error accessing files in IndexedDB:', event.target.error);
      reject(event.target.error);
    };
  });
};